import React from "react";
import styled from "styled-components";
import PixelIcon from "../PixelIcon";
import useSetting from "../../hooks/useSetting";
import { onKioskClockClick } from "../../kiosk";
import {
  getVolume,
  setVolume,
  click as clickSound,
  volumeDing,
} from "../../sound/sound";

// The Win95 taskbar: Start button, one button per open window, clock tray.
// Purely presentational — the Stage owns which windows exist, which is
// focused, and whether the Start menu is open.
//
// Sits in unzoomed-viewport space at the bottom of the screen. The bar is
// always full-width; window buttons shrink to fit like the real thing.

function formatClock(date) {
  let h = date.getHours();
  const suffix = h < 12 ? "AM" : "PM";
  h = h % 12 || 12;
  const m = String(date.getMinutes()).padStart(2, "0");
  return `${h}:${m} ${suffix}`;
}

function useClock() {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    // Tick on the minute boundary so the clock is never visibly stale.
    let timeout;
    const schedule = () => {
      const ms = 60000 - (Date.now() % 60000) + 50;
      timeout = setTimeout(() => {
        setNow(new Date());
        schedule();
      }, ms);
    };
    schedule();
    return () => clearTimeout(timeout);
  }, []);
  return now;
}

// The tray speaker: click for a little Win95 volume popup (slider +
// Mute). Shares state with the Settings panel via the setting event.
function TrayVolume() {
  const [open, setOpen] = React.useState(false);
  const [volSetting] = useSetting("saat.volume", getVolume());
  const vol = volSetting ?? 0.5;
  const lastNonZeroRef = React.useRef(vol > 0 ? vol : 0.5);
  // Effect, not render-time write: StrictMode double-renders would make
  // a render-phase ref mutation observably impure.
  React.useEffect(() => {
    if (vol > 0) lastNonZeroRef.current = vol;
  }, [vol]);

  return (
    <TrayVolumeWrap onMouseDown={(e) => e.stopPropagation()}>
      <TrayButton
        type="button"
        aria-label="Volume"
        title="Volume"
        onClick={() => {
          clickSound();
          setOpen((v) => !v);
        }}
      >
        <PixelIcon name="speaker" size={16} />
        {vol === 0 && <MuteX aria-hidden="true">×</MuteX>}
      </TrayButton>
      {open && <PopupBackdrop onMouseDown={() => setOpen(false)} />}
      {open && (
        <VolumePopup>
          {/* Modeled on the real Win95/98 tray popup (GUIdebook capture,
              81x136): no title bar, thin outline, "Volume" centered, a
              white loudness wedge beside the slider, a dotted focus box
              around the track, Mute with an underlined M. */}
          <VolumeTitle>Volume</VolumeTitle>
          <MidRow>
            <Wedge
              viewBox="0 0 14 84"
              width="14"
              height="84"
              aria-hidden="true"
            >
              <polygon
                points="1,2 12,2 12,82"
                fill="#fff"
                stroke="#808080"
                strokeWidth="1"
              />
            </Wedge>
            <TrackBox>
              <VSlider>
                <input
                  type="range"
                  className="has-box-indicator"
                  aria-label="Volume"
                  min="0"
                  max="100"
                  value={Math.round(vol * 100)}
                  onChange={(e) =>
                    setVolume(parseInt(e.target.value, 10) / 100)
                  }
                  // The real thing dings when you let go of the
                  // slider, so you hear the level you just picked.
                  onPointerUp={volumeDing}
                  onKeyUp={(e) => {
                    if (e.key.startsWith("Arrow")) volumeDing();
                  }}
                />
              </VSlider>
            </TrackBox>
          </MidRow>
          <MuteRow className="field-row">
            <input
              id="tray-mute"
              type="checkbox"
              checked={vol === 0}
              onChange={(e) =>
                setVolume(e.target.checked ? 0 : lastNonZeroRef.current)
              }
            />
            <label htmlFor="tray-mute">
              <u>M</u>ute
            </label>
          </MuteRow>
        </VolumePopup>
      )}
    </TrayVolumeWrap>
  );
}

function Taskbar({
  windows = [], // [{ id, label, active }]
  onWindowClick,
  onWindowContextMenu,
  startOpen = false,
  onStartToggle,
  className,
}) {
  const now = useClock();
  return (
    <Bar className={["window", className].filter(Boolean).join(" ")}>
      <StartButton
        type="button"
        data-open={startOpen ? "true" : undefined}
        data-scene-target="start"
        onClick={onStartToggle}
        // The Stage's outside-click handler closes the menu; stop the
        // mousedown here so opening and closing don't fight.
        onMouseDown={(e) => e.stopPropagation()}
      >
        <StartGlyph aria-hidden="true">
          <i /><i /><i /><i />
        </StartGlyph>
        Start
      </StartButton>
      <Divider />
      <WindowButtons>
        {windows.map((w) => (
          <WindowButton
            key={w.id}
            type="button"
            data-active={w.active ? "true" : undefined}
            data-taskbar-button={w.id}
            onClick={() => onWindowClick && onWindowClick(w.id)}
            onContextMenu={(e) => {
              e.preventDefault();
              // Keep the Stage's outside-click closer from immediately
              // eating the menu this opens.
              e.stopPropagation();
              if (onWindowContextMenu) onWindowContextMenu(w.id, e);
            }}
            title={w.label}
          >
            {w.icon}
            <ButtonLabel>{w.label}</ButtonLabel>
          </WindowButton>
        ))}
      </WindowButtons>
      <Tray>
        <TrayVolume />
        {/* 10 quick clicks toggle kiosk mode (see kiosk.js) — the
            mouse-only switch for keyboardless installs. */}
        <time onClick={onKioskClockClick}>{formatClock(now)}</time>
      </Tray>
    </Bar>
  );
}

export const TASKBAR_HEIGHT = 34;

const Bar = styled.div`
  /* Chrome text (clock, button labels) is furniture, not copy —
     dragging across the desktop must never highlight "4:12 PM". */
  -webkit-user-select: none;
  user-select: none;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  height: ${TASKBAR_HEIGHT}px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 3px 4px;
  /* The real taskbar has no side/bottom bevel — it only rises at the top. */
  border-left: none;
  border-right: none;
  border-bottom: none;
  box-shadow: inset 0 1px #fff;
`;

const StartButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: bold;
  min-width: 0;
  padding: 2px 8px 2px 5px;
  flex-shrink: 0;

  &&[data-open="true"] {
    box-shadow:
      inset -1px -1px #fff,
      inset 1px 1px #0a0a0a,
      inset -2px -2px #dfdfdf,
      inset 2px 2px #808080;
  }

  /* No dotted focus marquee from a mouse click — the pressed look is
     right, the black dots are wrong — but keep it for keyboard focus
     so tab-navigation stays visible. */
  &&:focus {
    outline: none;
  }
  &&:focus-visible {
    outline: 1px dotted #000;
    outline-offset: -4px;
  }
`;

// A four-pane "flag" drawn in CSS — enough to read as a start glyph
// without shipping an asset.
const StartGlyph = styled.span`
  display: grid;
  grid-template-columns: 7px 7px;
  grid-template-rows: 7px 7px;
  gap: 1px;
  transform: skewX(-8deg);

  & i {
    display: block;
  }
  & i:nth-child(1) {
    background: #d40000;
  }
  & i:nth-child(2) {
    background: #00a300;
  }
  & i:nth-child(3) {
    background: #0051c6;
  }
  & i:nth-child(4) {
    background: #ffbf00;
  }
`;

const Divider = styled.div`
  width: 2px;
  align-self: stretch;
  margin: 1px 0;
  border-left: 1px solid #808080;
  border-right: 1px solid #fff;
  flex-shrink: 0;
`;

const WindowButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 3px;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
`;

const WindowButton = styled.button`
  flex: 0 1 180px;
  min-width: 48px;
  padding: 2px 6px;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 5px;

  &&[data-active="true"] {
    font-weight: bold;
    background-image: repeating-conic-gradient(
      #fff 0% 25%,
      #dfdfdf 0% 50%
    );
    background-size: 2px 2px;
    box-shadow:
      inset -1px -1px #fff,
      inset 1px 1px #0a0a0a,
      inset -2px -2px #dfdfdf,
      inset 2px 2px #808080;
  }
`;

const ButtonLabel = styled.span`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Tray = styled.div`
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  box-shadow:
    inset -1px -1px #fff,
    inset 1px 1px #808080;
  font-variant-numeric: tabular-nums;
`;

const TrayVolumeWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const TrayButton = styled.button`
  position: relative;
  background: transparent;
  border: none;
  box-shadow: none;
  min-width: 0;
  min-height: 0;
  padding: 1px;
  display: flex;
  align-items: center;

  &&:not(:disabled):hover,
  &&:not(:disabled):active {
    box-shadow: none;
  }
  &&:focus {
    outline: none;
  }
`;

const MuteX = styled.span`
  position: absolute;
  right: -3px;
  bottom: -2px;
  color: #c00;
  font-weight: bold;
  font-size: 12px;
  text-shadow: 1px 1px 0 #fff;
`;

const PopupBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1250;
`;

const VolumePopup = styled.div`
  position: absolute;
  right: 0;
  bottom: ${TASKBAR_HEIGHT - 4}px;
  width: 88px;
  padding: 8px 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 1300;
  /* Not window chrome: the real popup has a plain thin outline with a
     white top-left sliver, not a raised 3D bevel. */
  background: silver;
  border: 1px solid #0a0a0a;
  box-shadow: inset 1px 1px 0 #fff;
`;

const MidRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Wedge = styled.svg`
  shape-rendering: crispEdges;
  flex-shrink: 0;
`;

// The dotted rectangle the real thing drew around the slider track.
const TrackBox = styled.div`
  border: 1px dotted #808080;
  padding: 6px 8px;
`;

// Our own vertical-slider geometry (98.css's .is-vertical assumes a
// surrounding layout we don't have): a fixed upright box with the
// horizontal input rotated a quarter turn about its center. min ends up
// at the bottom, which is where quiet belongs.
const VSlider = styled.div`
  position: relative;
  width: 21px;
  height: 76px;

  & > input[type="range"] {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 76px;
    height: 21px;
    margin: 0;
    transform: translate(-50%, -50%) rotate(270deg);
  }
`;

const MuteRow = styled.div`
  align-self: flex-start;
  margin-left: 6px;
`;

const VolumeTitle = styled.p`
  font-size: 11px;
  -webkit-user-select: none;
  user-select: none;
`;

export default Taskbar;
